import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  useTheme,
  Paper,
  Button,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { getBankTransactions } from "../../../../../services/Accounts/Safes";
import FilterAndSearch from "./StatementActions/FilterAndSearch";
import WithdraweModal from "./StatementActions/WithdraweModal";
import DepositModal from "./StatementActions/DepositModal";
import TransfermonyModal from "./StatementActions/TransfermonyModal";

const BankStatement = ({ bank }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openWithdrawe, setOpenWithdrawe] = useState(false);
  const [openDeposit, setOpenDeposit] = useState(false);
  const [openTransfermony, setOpenTransfermony] = useState(false);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const data = await getBankTransactions(bank.id);
      setTransactions(data.transactions || []);
      setFilteredTransactions(data.transactions || []);
      setTotals(data.totals || {});
    } catch (err) {
      setError("فشل في تحميل المعاملات.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (searchTerm) => {
    const filtered = transactions.filter((tx) =>
      Object.values(tx).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(searchTerm.trim().toLowerCase())
      )
    );
    setFilteredTransactions(filtered);
  };

  const handleFilterChange = (filterType) => {
    const filtered = filterType
      ? transactions.filter((tx) => tx.type === filterType)
      : transactions;
    setFilteredTransactions(filtered);
  };

  const handleDateRangeChange = ({ fromDate, toDate }) => {
    const filtered = transactions.filter((tx) => {
      const transactionDate = new Date(tx.transaction_date || tx.invoice?.date);
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;

      return (
        (!from || transactionDate >= from) && (!to || transactionDate <= to)
      );
    });
    setFilteredTransactions(filtered);
  };

  useEffect(() => {
    loadTransactions();
  }, [bank]);

  if (loading)
    return <CircularProgress sx={{ display: "block", mx: "auto", mt: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Paper
      elevation={isMobile ? 1 : 3}
      sx={{
        p: { xs: 0.75, sm: 1.5, md: 2 },
        borderRadius: 2,
        bgcolor: isDark ? "#1c1c2b" : "#f5f5f5",
        color: isDark ? "#fff" : "#232334",
        boxShadow: isMobile ? 2 : 4,
      }}
    >
      <Typography
        variant={isMobile ? "h6" : "h5"}
        fontWeight={700}
        gutterBottom
        sx={{
          textAlign: "center",
          color: isDark ? "#f48fb1" : "#1976d2",
          mb: { xs: 1, md: 1.5 },
          fontSize: { xs: "16px", sm: "18px", md: "20px" },
        }}
      >
        كشف حساب: {bank?.name}
      </Typography>

      <Box
        display="flex"
        alignItems="center"
        gap={{ xs: 0.75, sm: 1.5 }}
        mb={{ xs: 1.5, md: 2 }}
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <Box flex={1} width="100%">
          <FilterAndSearch
            onSearchChange={handleSearchChange}
            onFilterChange={handleFilterChange}
            onDateRangeChange={handleDateRangeChange}
          />
        </Box>
      </Box>

      {/* عرض البطاقات على الموبايل بدلاً من الجدول */}
      {isMobile ? (
        <Box sx={{ maxHeight: "calc(100vh - 280px)", overflowY: "auto" }}>
          {filteredTransactions.map((tx) => (
            <Card
              key={tx.id}
              sx={{
                mb: 1,
                bgcolor: isDark ? "#232334" : "#fff",
                boxShadow: 1,
              }}
            >
              <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" color="text.secondary" fontSize="10px">
                    {tx.transaction_date || tx.invoice?.date || "غير معروف"}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      color: tx.type === "withdrawal" ? "#d32f2f" : "#388e3c",
                      fontSize: "12px",
                    }}
                  >
                    {tx.amount?.toLocaleString() ||
                      tx.invoice?.total_amount?.toLocaleString() ||
                      "0"}{" "}
                    ج.م
                  </Typography>
                </Box>
                <Divider sx={{ my: 0.25 }} />
                <Typography variant="body2" fontSize="11px" mb={0.25}>
                  <strong>النوع:</strong>{" "}
                  {tx.type === "withdrawal"
                    ? "سحب"
                    : tx.type === "deposit"
                    ? "إيداع"
                    : tx.type === "transfer_out"
                    ? `ترحيل إلى ${tx.bank_to?.name || "غير معروف"}`
                    : tx.type === "transfer_in"
                    ? `إيداع من ${tx.bank_from?.name || "غير معروف"}`
                    : tx.type === "invoice"
                    ? `فاتورة رقم ${tx.invoice?.id || "غير معروف"}`
                    : "غير معروف"}
                </Typography>
                <Typography variant="body2" fontSize="11px" color="text.secondary">
                  <strong>المستخدم:</strong> {tx.user?.name || tx.invoice?.user_id || "غير معروف"}
                </Typography>
                {(tx.deposit_title || tx.deposit_description || tx.withdrawal_description) && (
                  <Typography variant="body2" fontSize="10px" color="text.secondary" mt={0.25}>
                    {tx.deposit_title || tx.deposit_description || tx.withdrawal_description}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        // الجدول للشاشات الكبيرة
        <Box sx={{ overflowX: "auto", maxHeight: "calc(100vh - 250px)", borderRadius: 2 }}>
          <Table size="small" sx={{ bgcolor: isDark ? "#232334" : "#fff", borderRadius: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: isDark ? "#f48fb1" : "#1976d2", fontSize: { sm: "12px", md: "13px" }, py: 0.75 }}>التاريخ</TableCell>
                <TableCell sx={{ fontWeight: 600, color: isDark ? "#f48fb1" : "#1976d2", fontSize: { sm: "12px", md: "13px" }, py: 0.75 }}>النوع</TableCell>
                <TableCell sx={{ fontWeight: 600, color: isDark ? "#f48fb1" : "#1976d2", fontSize: { sm: "12px", md: "13px" }, py: 0.75 }}>المبلغ</TableCell>
                <TableCell sx={{ fontWeight: 600, color: isDark ? "#f48fb1" : "#1976d2", fontSize: { sm: "12px", md: "13px" }, py: 0.75 }}>المستخدم</TableCell>
                <TableCell sx={{ fontWeight: 600, color: isDark ? "#f48fb1" : "#1976d2", fontSize: { sm: "12px", md: "13px" }, py: 0.75 }}>عنوان الإيداع</TableCell>
                <TableCell sx={{ fontWeight: 600, color: isDark ? "#f48fb1" : "#1976d2", fontSize: { sm: "12px", md: "13px" }, py: 0.75 }}>الوصف</TableCell>
                <TableCell sx={{ fontWeight: 600, color: isDark ? "#f48fb1" : "#1976d2", fontSize: { sm: "12px", md: "13px" }, py: 0.75 }}>ملاحظات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTransactions.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell sx={{ fontSize: { sm: "11px", md: "12px" }, py: 0.5 }}>{tx.transaction_date || tx.invoice?.date || "غير معروف"}</TableCell>
                  <TableCell sx={{ fontSize: { sm: "11px", md: "12px" }, py: 0.5 }}>
                    {tx.type === "withdrawal"
                      ? "سحب"
                      : tx.type === "deposit"
                      ? "إيداع"
                      : tx.type === "transfer_out"
                      ? `ترحيل إلى ${tx.bank_to?.name || "غير معروف"}`
                      : tx.type === "transfer_in"
                      ? `إيداع من ${tx.bank_from?.name || "غير معروف"}`
                      : tx.type === "invoice"
                      ? `فاتورة رقم ${tx.invoice?.id || "غير معروف"}`
                      : "غير معروف"}
                  </TableCell>
                  <TableCell sx={{ fontSize: { sm: "11px", md: "12px" }, py: 0.5 }}>{tx.amount?.toLocaleString() || tx.invoice?.total_amount?.toLocaleString() || "0"} ج.م</TableCell>
                  <TableCell sx={{ fontSize: { sm: "11px", md: "12px" }, py: 0.5 }}>{tx.user?.name || tx.invoice?.user_id || "غير معروف"}</TableCell>
                  <TableCell sx={{ fontSize: { sm: "11px", md: "12px" }, py: 0.5 }}>{tx.deposit_title || "لا يوجد"}</TableCell>
                  <TableCell sx={{ fontSize: { sm: "11px", md: "12px" }, py: 0.5 }}>{tx.deposit_description || tx.withdrawal_description || tx.invoice?.service || "لا يوجد"}</TableCell>
                  <TableCell sx={{ fontSize: { sm: "11px", md: "12px" }, py: 0.5 }}>{tx.note || tx.invoice?.discount || "لا توجد ملاحظات"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Box
        mt={{ xs: 1.5, md: 2 }}
        sx={{
          position: "sticky",
          bottom: 0,
          bgcolor: isDark ? "#232334" : "#f5f5f5",
          color: isDark ? "#fff" : "#232334",
          boxShadow: isMobile ? 1 : 3,
          p: { xs: 1, sm: 1.5 },
          borderRadius: 2,
        }}
      >
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          sx={{
            color: isDark ? "#f48fb1" : "#1976d2",
            textAlign: "center",
            fontWeight: 700,
            mb: { xs: 1, md: 1.5 },
            fontSize: { xs: "14px", sm: "16px", md: "18px" },
          }}
        >
          الإجماليات
        </Typography>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
          gap={{ xs: 1, sm: 1.5 }}
        >
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: "#888",
                fontSize: { xs: "11px", sm: "12px", md: "13px" },
              }}
            >
              الإجمالي من الفواتير:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: isDark ? "#f48fb1" : "#1976d2",
                fontSize: { xs: "12px", sm: "13px" },
              }}
            >
              {totals.invoice?.toLocaleString() || 0} ج.م
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: "#888",
                fontSize: { xs: "11px", sm: "12px", md: "13px" },
              }}
            >
              الإجمالي من الإيداعات:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: isDark ? "#4caf50" : "#388e3c",
                fontSize: { xs: "12px", sm: "13px" },
              }}
            >
              {totals.deposit?.toLocaleString() || 0} ج.م
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: "#888",
                fontSize: { xs: "11px", sm: "12px", md: "13px" },
              }}
            >
              الإجمالي من السحوبات:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: isDark ? "#ff5722" : "#d32f2f",
                fontSize: { xs: "12px", sm: "13px" },
              }}
            >
              {totals.withdrawal?.toLocaleString() || 0} ج.م
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: "#888",
                fontSize: { xs: "11px", sm: "12px", md: "13px" },
              }}
            >
              الإجمالي من الترحيل للخارج:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: isDark ? "#ff9800" : "#f57c00",
                fontSize: { xs: "12px", sm: "13px" },
              }}
            >
              {totals.transfer_out?.toLocaleString() || 0} ج.م
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: "#888",
                fontSize: { xs: "11px", sm: "12px", md: "13px" },
              }}
            >
              الإجمالي من الترحيل للداخل:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: isDark ? "#03a9f4" : "#0288d1",
                fontSize: { xs: "12px", sm: "13px" },
              }}
            >
              {totals.transfer_in?.toLocaleString() || 0} ج.م
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: "#888",
                fontSize: { xs: "11px", sm: "12px", md: "13px" },
              }}
            >
              الإجمالي الخارج من الخزنة:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: isDark ? "#e91e63" : "#c2185b",
                fontSize: { xs: "12px", sm: "13px" },
              }}
            >
              {totals.total_out?.toLocaleString() || 0} ج.م
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="body2"
              fontWeight={900}
              sx={{
                color: "#FF0000",
                fontSize: { xs: "11px", sm: "12px", md: "13px" },
              }}
            >
              الإجمالي الكلي للدخل:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={999}
              sx={{
                color: isDark ? "#8bc34a" : "#689f38",
                fontSize: { xs: "13px", sm: "14px" },
              }}
            >
              {totals.total_income?.toLocaleString() || 0} ج.م
            </Typography>
          </Box>
        </Box>
        <Box
          mt={{ xs: 1.5, md: 2 }}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            gap: { xs: 0.75, sm: 1.5 },
          }}
        >
          <Button
            variant="contained"
            color="error"
            onClick={() => setOpenWithdrawe(true)}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: "12px", sm: "13px" }, py: { xs: 0.6, sm: 0.8 } }}
          >
            سحب
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => setOpenDeposit(true)}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: "12px", sm: "13px" }, py: { xs: 0.6, sm: 0.8 } }}
          >
            إيداع
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenTransfermony(true)}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ fontSize: { xs: "12px", sm: "13px" }, py: { xs: 0.6, sm: 0.8 } }}
          >
            ترحيل
          </Button>
        </Box>

        {/* Modals for Withdrawe, Deposit, and Transfermony */}
        {openWithdrawe && (
          <WithdraweModal
            open={openWithdrawe}
            onClose={() => setOpenWithdrawe(false)}
            bank_id={bank.id}
            onSuccess={loadTransactions}
          />
        )}
        {openDeposit && (
          <DepositModal
            open={openDeposit}
            onClose={() => setOpenDeposit(false)}
            bank={bank} // ✅ هنا كنت كاتب bank_id ❌، والصحيح تمرر كائن البنك كله
            onSuccess={loadTransactions}
          />
        )}
        {openTransfermony && (
          <TransfermonyModal
            open={openTransfermony}
            onClose={() => setOpenTransfermony(false)}
            from_bank_id={bank.id}
            onSuccess={loadTransactions}
          />
        )}
      </Box>
    </Paper>
  );
};

export default BankStatement;
